import { BrowserRouter as Router, Routes, Route, useSearchParams, useLocation } from 'react-router-dom';
import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import { initializeTheme } from './utils/themeLoader';
import { fetchHotelIdByDomain } from './services/accommodationApi';
import { TranslationProvider } from './context/TranslationContext';

// Homepage components - loaded immediately (always needed)
import DesktopComponent from './components/DesktopComponent';
import AvailabilityComponent from './components/AvailabilityComponent';
import SessionExpiryGuard from './components/SessionExpiryGuard';

// Other pages - lazy loaded (only when navigated to)
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const UnifiedCheckout = lazy(() => import('./components/UnifiedCheckout'));
const ConfirmationPage = lazy(() => import('./components/ConfirmationPage'));
const CityPage = lazy(() => import('./components/CityPage'));
const Showcase = lazy(() => import('./components/Showcase'));

// Minimal full-page loader for lazy route transitions
const PageLoader = () => (
  <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
  </div>
);

// Theme Loader Component - Loads theme CSS immediately on mount
// Must be inside Router to use useSearchParams
function ThemeLoader() {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    initializeTheme(searchParams, null, null);
  }, [searchParams, location]);

  return null;
}

// Auto-detect mobile or desktop component
function AutoDetectComponent() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const checkMobile = () => window.innerWidth <= 768;

    setIsMobile(checkMobile());

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsMobile(checkMobile());
      }, 150);
    };

    const handleOrientation = () => setIsMobile(checkMobile());

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientation);

    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, []);

  // App-like viewport on TOUCH mobile only: disable pinch/double-tap zoom + safe-area.
  // A narrow non-touch desktop keeps zoom (a11y) — gate on pointer:coarse, not width alone.
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    if (!vp) return;
    const isTouch = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    vp.setAttribute(
      'content',
      (isMobile && isTouch)
        ? 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        : 'width=device-width, initial-scale=1, viewport-fit=cover'
    );
  }, [isMobile]);

  return isMobile ? <AvailabilityComponent /> : <DesktopComponent />;
}

// Home entry: if there is no hotel_id in the URL, try to resolve it from the
// current domain (white-label deployments like bookings.soluna-hotels.com).
// Resolved id is injected into the URL so all downstream code works unchanged.
// If the domain is not configured, fall through — components show "Hotel ID
// is required" exactly as before.
function HomeRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasHotelId = !!(searchParams.get('hotel_id') || searchParams.get('accommodationid'));
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (hasHotelId) return;
    let cancelled = false;
    (async () => {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const id = await fetchHotelIdByDomain(hostname);
      if (cancelled) return;
      if (id) {
        const next = new URLSearchParams(searchParams);
        next.set('hotel_id', id);
        setSearchParams(next, { replace: true });
      }
      setAttempted(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the loader until hotel_id is actually in the URL (resolved + injected)
  // or the domain lookup has finished without a match. This guarantees the
  // home component mounts ONLY after hotel_id is in the URL, so its own mount
  // effect (which rewrites search params) cannot strip the injected hotel_id.
  if (!hasHotelId && !attempted) return <PageLoader />;
  return <AutoDetectComponent />;
}

function App() {
  return (
    <TranslationProvider>
      <Router>
        <ThemeLoader />
        <SessionExpiryGuard />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/checkout-page" element={<UnifiedCheckout />} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            <Route path="/booking-details" element={<ConfirmationPage />} />
            <Route path="/city" element={<CityPage />} />
            <Route path="/showcase" element={<Showcase />} />
            {/* Brand-first Our Hotels page: /<groupname> e.g. /capitalhotels.
                Static routes above rank higher, so they are never shadowed. */}
            <Route path="/:group" element={<CityPage />} />
            <Route path="*" element={<HomeRoute />} />
          </Routes>
        </Suspense>
      </Router>
    </TranslationProvider>
  );
}

export default App;
